import React, { JSX } from 'react';
import { styled } from '@mui/material/styles';
import { IClientMessage } from 'src/interfaces';
import { text } from 'src/utils';
import classNames from 'classnames';

/**
 * MessageExtra props.
 * @typedef {Object} IMessageExtraProps
 * @property {React.ReactNode} [customExtra] - Extra content to show below the message
 * @property {boolean} [hasDebug] - When true, shows the message JSON (debug mode)
 * @property {IClientMessage} [message] - Message used for the debug dump
 * @property {'full'} [position] - When 'full', spans the entire extra row in the grid
 */
export type IMessageExtraProps = Omit<
	JSX.IntrinsicElements['footer'],
	'children'
> & {
	customExtra?: React.ReactNode;
	hasDebug?: boolean;
	message?: IClientMessage;
	position?: 'full';
};

/**
 * Extra area below the message: custom content and/or message debug dump.
 *
 * **Behaviour:**
 * - Always renders `customExtra` when provided.
 * - When `hasDebug` is true and `message` is set, adds a `<pre>` with the text representation of the message (for debug, toggled via long-press on the footer time).
 * - Click on the element calls stopPropagation to prevent bubbling.
 * - When `position === 'full'`, the MessageExtra-full class expands the area to the full grid row (extra-left / extra-right). Default: 'extra' area only.
 *
 * @param {IMessageExtraProps} props - customExtra, hasDebug, message, position and footer attributes
 * @returns {JSX.Element} Footer element with extra content and optional debug pre
 */
export const MessageExtra = styled(
	({
		customExtra,
		hasDebug = false,
		message,
		position,
		onClick,
		className,
		...props
	}: IMessageExtraProps) => (
		<footer
			className={classNames(
				className,
				position === 'full' && 'MessageExtra-full',
			)}
			onClick={(event) => {
				event.stopPropagation();
				onClick?.(event);
			}}
			{...props}
		>
			{customExtra}
			{hasDebug && message ? <pre>{text(message)}</pre> : null}
		</footer>
	),
	{
		label: 'MessageExtra',
		target: 'MessageExtra-root',
	},
)({
	gridArea: 'extra',
	'&.MessageExtra-full': {
		gridArea: 'extra-left / extra-left / extra-right / extra-right',
	},
});
